import { Component } from "@kluntje/core";
import { find, getParent, waitForTransitionEnd } from "@kluntje/js-utils/lib/dom-helpers";
import { ACTIVE } from "Helper/cssClasses";

export class FraAccordion extends Component {
  /**
   * Indicates whether only one item can be opened at a time
   */
  get isExclusive(): boolean {
    return this.hasAttribute("exclusive");
  }

  /**
   * Indicates whether the currently opened item can be closed in an exclusive accordion
   */
  get isAlwaysOpen(): boolean {
    return this.getAttribute("exclusive") === "always-open";
  }

  /**
   * return list of accordion sections which are open (have the ACTIVE class)
   *
   * @readonly
   * @type {HTMLElement[]}
   * @memberof FraAccordion
   */
  get openSections(): HTMLElement[] {
    return Array.from(this.ui.sections as NodeListOf<HTMLElement>).filter(section =>
      section.classList.contains(ACTIVE),
    );
  }

  constructor() {
    super({
      ui: {
        toggles: ".fra-js-accordion__toggle",
        sections: ".fra-js-accordion__section",
      },
      events: [
        {
          event: "click",
          target: "toggles",
          handler: "handleSectionToggle",
        },
      ],
    });
  }

  /**
   * Lifecycle-Hook
   * @override
   */
  afterComponentRender() {
    // support anchor functionality
    if (window.location.hash) {
      const targetNode = [...this.ui.sections].find(node => node.id === window.location.hash.substring(1));
      if (targetNode) {
        const toggle = find(targetNode, ".fra-m-accordion__toggle");
        if (toggle) toggle.click();
      }
    }
  }

  /**
   * handle Click on Section Toggle,
   * open/ close sections
   * @param {MouseEvent} e
   */
  handleSectionToggle(e: Event): void {
    // Check whether link inside accordion toggle was clicked eg. in  flight info
    const linkElement = getParent(e.target as HTMLElement, ".fra-e-link");
    if (linkElement) return;

    e.preventDefault();
    const currentSection = getParent(e.target as HTMLElement, ".fra-js-accordion__section") as HTMLElement;

    // user is not allowed to close the only open section in `always-open` mode
    if (this.isAlwaysOpen && this.openSections.includes(currentSection)) return;

    const otherSections = this.openSections.filter(section => section !== currentSection);

    if (this.isExclusive) {
      // close any other open section
      otherSections.forEach(section => this.toggleSection(section, false));
    }

    // current section is already open, it will then be closed
    const alreadyOpen = this.openSections.includes(currentSection);
    this.toggleSection(currentSection, !alreadyOpen);
  }

  /**
   * returns the distance from the top of the accordion section to the page root,
   * ignoring other open section in `exclusive` mode
   * and taking into account whether or not the sticky header is visible
   *
   * @param {HTMLElement} section - accordion section which its top distance needs to be calculated
   * @returns {number}
   */
  getScrollTop(section: HTMLElement): number {
    // other section which will be closed when the new section opens
    const closedSections: HTMLElement[] = [];
    // only the case in the `exclusive` setup
    if (this.isExclusive) {
      closedSections.push(...this.openSections);
      // temporarily close for the calculation
      closedSections.forEach(s => s.classList.remove(ACTIVE));
    }
    let offsetTop = section.offsetTop;

    // reset the temporarily closed items
    closedSections.forEach(s => s.classList.add(ACTIVE));

    const willScrollDown = offsetTop > window.scrollY;
    // remove the height of the header which will be moved out of view
    if (willScrollDown) offsetTop += document.querySelector<HTMLElement>("fra-m-page-header")?.offsetHeight || 0;

    return offsetTop;
  }

  /**
   * open or closes the given section depending on the open parameter with some animation
   *
   * @param {(HTMLElement | null)} section - accordion section to be opened or closed.
   *                                         null if it is not a proper accordion markup
   * @param {boolean} open - true: open section. false: close section
   */
  toggleSection(section: HTMLElement | null, open: boolean) {
    if (section === null) return;

    const content = section.querySelector<HTMLElement>(".fra-m-accordion__section-content")!;

    if (open) {
      // scroll distance needed to align section top of view / header when it gets open (and others closed)
      const scrollTop = this.getScrollTop(section);
      section.classList.add(ACTIVE);

      setTimeout(() => {
        window.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });
      }, 200); // start scrolling in the middle of opening animation
    }

    // will have a transition by changing the height and padding to their end values from css
    const height = `${content.offsetHeight}px`;
    const { paddingBottom, paddingTop } = window.getComputedStyle(content);

    const startHeight = open ? "0" : height;
    const endHeight = open ? height : "0";

    const startPaddingTop = open ? "0" : paddingTop;
    const endPaddingTop = open ? paddingTop : "0";
    const startPaddingBottom = open ? "0" : paddingBottom;
    const endPaddingBottom = open ? paddingBottom : "0";
    const startOpacity = open ? "0" : "1";
    const endOpacity = open ? "1" : "0";

    content.style.height = startHeight;
    content.style.paddingTop = startPaddingTop;
    content.style.paddingBottom = startPaddingBottom;
    content.style.opacity = startOpacity;
    content.style.overflow = "hidden";
    // make initial inline styling applied
    window.getComputedStyle(content);

    requestAnimationFrame(async () => {
      content.style.transition = "height .4s ease-in-out, padding .4s ease-in-out, opacity .4s ease-in-out";
      content.style.height = endHeight;
      content.style.paddingTop = endPaddingTop;
      content.style.paddingBottom = endPaddingBottom;
      content.style.opacity = endOpacity;

      await waitForTransitionEnd(content, "height");

      if (!open) section.classList.remove(ACTIVE);
      // clean up inline styles from the animation
      content.style.height = "";
      content.style.overflow = "";
      content.style.paddingTop = "";
      content.style.paddingBottom = "";
      content.style.transition = "";
      content.style.opacity = "";
    });
  }
}

customElements.define("fra-m-accordion", FraAccordion);
